import { getFormProps, getInputProps, useForm } from '@conform-to/react';
import { getZodConstraint, parseWithZod } from '@conform-to/zod';
import {
  type ActionFunctionArgs,
  type LoaderFunctionArgs,
  json,
} from '@remix-run/node';
import { Form, Link, useActionData, useSearchParams } from '@remix-run/react';
import { z } from 'zod';
import { Button } from '~/components/Button';
import { CardBase, CardBody, CardHeader } from '~/components/Card';
import { Input } from '~/components/Forms/BaseInput';
import { Label } from '~/components/Forms/Label';
import TextField, { TextFieldWrapper } from '~/components/Forms/TextField';
import LadderLogo from '~/components/LadderLogo';
import { requireAnonymousUser } from '~/server/auth/authenticate-user';
import { signIn } from '~/server/auth/sign-in';
import { handleNewSession } from '~/server/login.server';
import { useIsPending } from '~/utils/misc';
import { EmailSchema, PasswordSchema } from '~/utils/user-validation';

const LoginFormSchema = z.object({
  email: EmailSchema,
  password: PasswordSchema,
  redirectTo: z.string().optional(),
});

export async function loader({ request }: LoaderFunctionArgs) {
  await requireAnonymousUser(request); // Only see this page if not logged in
  return {};
}

export async function action({ request }: ActionFunctionArgs) {
  await requireAnonymousUser(request);
  const formData = await request.formData();
  const submission = await parseWithZod(formData, {
    schema: (intent) =>
      LoginFormSchema.transform(async (data, ctx) => {
        if (intent !== null) return { ...data, sessionCookie: null };

        const sessionCookie = await signIn(request, data);
        if (!sessionCookie) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Invalid email or password',
          });
          return z.NEVER;
        }
        return { ...data, sessionCookie };
      }),
    async: true,
  });
  if (submission.status !== 'success' || !submission.value.sessionCookie) {
    return json(
      {
        result: submission.reply(),
      },
      {
        status: submission.status === 'error' ? 400 : 200,
      }
    );
  }

  const { sessionCookie, redirectTo } = submission.value;

  return handleNewSession({
    request,
    sessionCookie,
    redirectTo,
  });
}

export default function LoginPage() {
  const actionData = useActionData<typeof action>();
  const [searchParams] = useSearchParams();
  const isPending = useIsPending();
  const redirectTo = searchParams.get('redirectTo');
  const [form, fields] = useForm({
    id: 'login-form',
    constraint: getZodConstraint(LoginFormSchema),
    defaultValue: {
      redirectTo,
    },
    lastResult: actionData?.result,
    onValidate({ formData }) {
      return parseWithZod(formData, { schema: LoginFormSchema });
    },
    shouldRevalidate: 'onBlur',
  });

  return (
    <CardBase className="w-full min-w-[416px] max-w-xl p-4">
      <CardHeader>
        <LadderLogo size="md" />
      </CardHeader>
      <CardBody>
        {form?.errors && <div className="text-red-500">{form.errors}</div>}
        <Form
          method="post"
          {...getFormProps(form)}
          className="grid grid-cols-1 gap-md"
        >
          <TextField
            label="Username"
            name="email"
            inputProps={{ ...getInputProps(fields.email, { type: 'email' }) }}
          />

          <TextFieldWrapper>
            <div className="flex justify-between items-baseline ">
              <Label className="text-inherit" htmlFor="password">
                Password
              </Label>
              <Link
                to="/forgot-password"
                className="text-xs text-secondary hover:text-primary leading-none"
                tabIndex={-3}
              >
                Forgot password?
              </Link>
            </div>
            <Input {...getInputProps(fields.password, { type: 'password' })} />
          </TextFieldWrapper>
          <input {...getInputProps(fields.redirectTo, { type: 'hidden' })} />

          <Button
            variant="primary"
            type="submit"
            className="mt-md"
            disabled={isPending}
          >
            {isPending ? 'Loading...' : 'Sign In'}
          </Button>
        </Form>
      </CardBody>
    </CardBase>
  );
}
